'use client';

import iconCoinPrimary from '@/assets/images/icons/coin-primary.png';
import iconCoinSmall from '@/assets/images/icons/coin-small.png';
import { useEffect, useRef, useState } from 'react';
import FaIcon, { faExclamationCircle } from '../../../../components/icon/FaIcon/FaIcon';
import IconTextUnit from '../../../../components/ui/IconTextUnit/IconTextUnit';
import { swal } from '../../../../lib';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import { formatCurrency } from '../../../../utils';
import { pickErrorMessages, scrollIntoTheView } from '../../../../utils/functions';
import type { PostCalcCoinParams, PostCalcCoinResponse } from '../../api/postCalcCoin';
import type {
  CalcCoinResult,
  LotteryBenefit,
  LotteryDetailAtomHook,
  LotteryDetailLoadingAtomHook,
  MultiDrawItem,
} from '../../types';
import { totalStock } from '../../utils';
import styles from './LotteryDrawSelect.module.scss';

function LotteryDrawSelect({
  lotteryEventId,
  drawFee,
  multiDraw,
  benefit,
  calcResult,
  currentOwnCoin,
  storageKey,
  isFromFamipay,
  useLotteryDetailAtom,
  useLotteryDetailLoadingAtom,
  postCalcCoin,
}: {
  lotteryEventId: number;
  drawFee: number;
  multiDraw: Array<MultiDrawItem>;
  benefit: Array<LotteryBenefit>;
  calcResult: CalcCoinResult;
  currentOwnCoin: number;
  storageKey: string;
  isFromFamipay: boolean;
  useLotteryDetailAtom: LotteryDetailAtomHook;
  useLotteryDetailLoadingAtom: LotteryDetailLoadingAtomHook;
  postCalcCoin: (params: PostCalcCoinParams) => Promise<PostCalcCoinResponse>;
}) {
  const [multiDrawId, setMultiDrawId] = useState(0);

  const [, setLotteryDetail] = useLotteryDetailAtom();
  const [, setLotteryDetailLoading] = useLotteryDetailLoadingAtom();
  const isFetchingCoinMenu = useRef(false);

  const setDrawMenuId = async (multiDrawId: number, drawCount: number, prizeLabelId = 0) => {
    setMultiDrawId(multiDrawId);
    await calcCoin(multiDrawId, drawCount, lotteryEventId, prizeLabelId);
  };

  const clickItem = async (e: React.MouseEvent, multiDrawId: number, drawCount: number, prizeLabelId = 0) => {
    e.preventDefault();
    if (isFetchingCoinMenu.current) return;
    setDrawMenuId(multiDrawId, drawCount, prizeLabelId);
    localStorage.setItem(storageKey, multiDrawId.toString());
    scrollIntoTheView('draw-confirm-head');
  };

  useEffect(() => {
    (async () => {
      setLotteryDetail({
        lotteryEventId: lotteryEventId,
        multiDrawId: 0,
        drawCount: 1,
        drawCoin: calcResult.drawCoin,
        shippingCoin: calcResult.shippingCoin,
        drawTotalCoin: calcResult.drawCoin + calcResult.shippingCoin,
        prizeLabelName: '',
      });

      // 念の為クリア上限を設ける
      if (localStorage.length > 1000) {
        localStorage.clear();
      }

      // 前回選択したメニューがある場合はそれを初期値としてセット
      const prevSelectItemId = Number(localStorage.getItem(storageKey));
      if (prevSelectItemId) {
        if (prevSelectItemId === 0) {
          // 単発
          setDrawMenuId(0, 1);
          return;
        }
        const prevSelectItem = multiDraw.find((item) => item.id === Number(prevSelectItemId));
        if (prevSelectItem) {
          setDrawMenuId(prevSelectItem?.id, prevSelectItem?.count, prevSelectItem?.prizeLabelId);
        }
      } else {
        // ない場合は現在所持しているコインで引ける最大回数のメニューを選択
        await setMaxDrawableMenuId(lotteryEventId);
      }
    })();
  }, [calcResult, lotteryEventId, setLotteryDetail]);

  // コイン計算
  const calcCoin = async (multiDrawId: number, drawCount: number, lotteryEventId: number, prizeLabelId = 0) => {
    try {
      setLotteryDetailLoading(true);
      const { result } = await postCalcCoin({
        lotteryEventId: lotteryEventId,
        drawCount: drawCount,
      });

      let benefitPrizeName = findBenefit(prizeLabelId, benefit)?.name ?? '';

      // 特典なし
      if (prizeLabelId && findBenefitTotalStock(prizeLabelId, benefit) === 0) {
        benefitPrizeName = '在庫なし';
      }

      setLotteryDetail({
        lotteryEventId: lotteryEventId,
        multiDrawId: multiDrawId,
        drawCount: drawCount,
        drawCoin: result.drawCoin,
        shippingCoin: result.shippingCoin,
        drawTotalCoin: result.drawCoin + result.shippingCoin,
        prizeLabelName: benefitPrizeName,
      });
    } catch (error) {
      await swal.messages({ messages: pickErrorMessages(error) });
    } finally {
      setLotteryDetailLoading(false);
    }
  };

  // 今のコインで引ける最大回数のメニューにチェック
  const setMaxDrawableMenuId = async (lotteryEventId: number) => {
    try {
      if (isFetchingCoinMenu.current) return;

      const currentCoin = currentOwnCoin;
      setLotteryDetailLoading(true);
      for (const item of multiDraw.filter((item) => item.count * drawFee <= currentCoin)) {
        const { result } = await postCalcCoin({
          lotteryEventId: lotteryEventId,
          drawCount: item.count,
        });

        if (currentCoin < result.drawCoin + result.shippingCoin) {
          continue;
        }
        let benefitPrizeName = findBenefit(item.prizeLabelId, benefit)?.name ?? '';

        // 特典なし
        if (item.prizeLabelId && findBenefitTotalStock(item.prizeLabelId, benefit) === 0) {
          benefitPrizeName = '在庫なし';
        }

        setLotteryDetail({
          lotteryEventId: lotteryEventId,
          multiDrawId: item.id,
          drawCount: item.count,
          drawCoin: result.drawCoin,
          shippingCoin: result.shippingCoin,
          drawTotalCoin: result.drawCoin + result.shippingCoin,
          prizeLabelName: benefitPrizeName,
        });
        setMultiDrawId(item.id);
        break;
      }
    } catch (error) {
      await swal.messages({ messages: pickErrorMessages(error) });
    } finally {
      setLotteryDetailLoading(false);
      isFetchingCoinMenu.current = false;
    }
  };

  return (
    <div className={styles.drawSelect}>
      {/* 多連 */}
      {multiDraw.map((item) => (
        // biome-ignore lint/a11y/useValidAnchor: <explanation>
        <a
          onClick={(e) => clickItem(e, item.id, item.count, item.prizeLabelId)}
          key={item.id}
          href='#'
          className={`${styles.drawSelectItem} ${multiDrawId === item.id ? styles.isActive : ''}`}
          data-testid={`bonus-link-${item.count}`}
        >
          {/* 丸 */}
          <div className={styles.drawButtonCircle} />
          {/* 上部 */}
          <div className={styles.drawButtonTop}>
            <div className={`${styles.drawButtonCount} ${utilsStyles.textBold}`}>
              {item.name ? (
                <>
                  {/* ボタン名称あり */}
                  <small>{item.name}</small>
                </>
              ) : (
                <>
                  {/* ボタン名称なし */}
                  <em>{item.count}</em>
                  <small style={{ marginTop: '0.4em' }}>回連続</small>
                </>
              )}
            </div>

            <div className={styles.drawButtonCoin}>
              <IconTextUnit
                text={formatCurrency(item.count * drawFee)}
                imgSrc={multiDrawId === item.id && !isFromFamipay ? iconCoinPrimary.src : iconCoinSmall.src}
              />
            </div>
          </div>
          {/* 下部 */}
          {item.prizeLabelId ? (
            <>
              {/* 下部（特典あり) */}
              <div className={styles.drawButtonBottom}>
                {/* 在庫なし */}
                {findBenefitTotalStock(item.prizeLabelId, benefit) === 0 ? (
                  <>
                    <p className={utilsStyles.textBold}>【特典内容】</p>
                    <FaIcon icon={faExclamationCircle} size='lg' />
                    {item.count}連特典が在庫切れのため特典は付与されません。
                  </>
                ) : (
                  <div>
                    <p className={utilsStyles.textBold}>【特典内容】</p>
                    {findBenefit(item.prizeLabelId, benefit)?.name ?? ''}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* 下部（特典なし) */}
              <div className={styles.drawButtonBottom}>
                <p className={utilsStyles.textBold}>【特典内容】</p>
                特典なし
              </div>
            </>
          )}
        </a>
      ))}

      {/* 単発 */}
      {/* biome-ignore lint/a11y/useValidAnchor: <explanation> */}
      <a
        onClick={(e) => clickItem(e, 0, 1)}
        href='#'
        className={`${styles.drawSelectItem} ${multiDrawId === 0 ? styles.isActive : ''}`}
        data-testid='bonus-link-1'
      >
        {/* 丸 */}
        <div className={styles.drawButtonCircle} />
        {/* 上部 */}
        <div className={styles.drawButtonTop}>
          <div className={`${styles.drawButtonCount} ${utilsStyles.textBold}`}>
            <em>1</em>
            <small style={{ marginTop: '0.4em' }}>回</small>
          </div>
          <div className={styles.drawButtonCoin}>
            <IconTextUnit
              text={formatCurrency(drawFee)}
              imgSrc={multiDrawId === 0 && !isFromFamipay ? iconCoinPrimary.src : iconCoinSmall.src}
            />
          </div>
        </div>
        {/* 下部 */}
        <div className={styles.drawButtonBottom}>
          <p className={utilsStyles.textBold}>【特典内容】</p>
          特典なし
        </div>
      </a>
    </div>
  );
}

function findBenefitTotalStock(prizeLabelId: number, benefits: Array<LotteryBenefit>) {
  const benefit = benefits.find((v) => v.id === prizeLabelId);
  if (benefit) {
    return totalStock(benefit.goods);
  }
  return 0;
}

function findBenefit(prizeLabelId: number, benefits: Array<LotteryBenefit>) {
  return benefits.find((v) => v.id === prizeLabelId);
}

export default LotteryDrawSelect;
